import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw, DollarSign,
  Layers, Package, FileBarChart, Calendar, ChevronRight, X as CloseIcon,
  Receipt, Zap, Truck, BoxSelect, Users as UsersIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtINR = v => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(v || 0);
const fmt = v => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 }).format(v || 0);

const CATEGORY_ICON = {
  'Electricity': Zap,
  'Freight-Transport': Truck,
  'Packaging': BoxSelect,
  'Salary-Wages': UsersIcon,
  'Rent': Receipt,
  'Utilities': Zap,
  'Machinery-Maintenance': Receipt,
  'Marketing': TrendingUp,
  'Professional Fees': Receipt,
  'Office': Receipt,
  'Other': Receipt,
};

export default function CostVariance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(5);
  const [tab, setTab] = useState('overheads');
  const [drillCategory, setDrillCategory] = useState(null);  // string

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cost-variance/report`, {
        params: { threshold_pct: threshold },
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold]);

  const kpi = data?.kpi || {};

  const overheadChart = useMemo(
    () => (data?.categories || []).slice(0, 8).map(c => ({
      name: c.category.length > 14 ? c.category.slice(0, 13) + '…' : c.category,
      prev: c.prev_month || 0,
      this: c.this_month || 0,
    })),
    [data]
  );

  return (
    <div data-testid="cost-variance-page" className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <FileBarChart className="text-amber-400" size={24} /> Cost Variance (Detailed)
          </h1>
          <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2">
            <Calendar size={13} /> {data?.prev_month || '…'} <span className="text-zinc-600">→</span> {data?.this_month || '…'}
            · flag threshold ±{threshold}%
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="cv-threshold"
            value={threshold}
            onChange={e => setThreshold(parseFloat(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 text-zinc-100 text-xs rounded-lg px-3 py-1.5"
          >
            <option value={3}>±3% threshold</option>
            <option value={5}>±5% threshold</option>
            <option value={10}>±10% threshold</option>
            <option value={15}>±15% threshold</option>
          </select>
          <button
            data-testid="cv-refresh"
            onClick={load}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
          ><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="This Month Spend" value={`₹${fmtINR(kpi.total_this_month)}`} testid="cv-kpi-this" />
        <KPI label="Last Month Spend" value={`₹${fmtINR(kpi.total_prev_month)}`} testid="cv-kpi-prev" />
        <KPI
          label="Total Variance"
          value={kpi.total_variance_pct !== null && kpi.total_variance_pct !== undefined
            ? `${kpi.total_variance_pct > 0 ? '+' : ''}${kpi.total_variance_pct}%`
            : '—'}
          color={kpi.total_variance_pct > 0 ? 'text-red-400' : kpi.total_variance_pct < 0 ? 'text-green-400' : 'text-zinc-100'}
          testid="cv-kpi-var"
        />
        <KPI label="Flagged (> threshold)" value={kpi.total_flags || 0} color="text-amber-400" testid="cv-kpi-flags" />
      </div>

      {/* Sub-totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Raw Material (this)" value={`₹${fmtINR(kpi.rm_this_total)}`} color="text-sky-400" testid="cv-kpi-rm-this" />
        <KPI label="Raw Material (prev)" value={`₹${fmtINR(kpi.rm_prev_total)}`} testid="cv-kpi-rm-prev" />
        <KPI label="Overheads (this)" value={`₹${fmtINR(kpi.overhead_this_total)}`} color="text-orange-400" testid="cv-kpi-oh-this" />
        <KPI label="Overheads (prev)" value={`₹${fmtINR(kpi.overhead_prev_total)}`} testid="cv-kpi-oh-prev" />
      </div>

      {/* ── Cost per kg produced ───────────────────────────────────────── */}
      {data?.cost_per_kg && <CostPerKgPanel cpk={data.cost_per_kg} prevMonth={data.prev_month} thisMonth={data.this_month} />}

      {/* Chart — overhead categories */}
      {overheadChart.length > 0 && (
        <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
          <div className="text-sm font-semibold text-zinc-100 mb-2">Overhead categories · {data?.prev_month} vs {data?.this_month}</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={overheadChart} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
              <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip
                contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8 }}
                formatter={(v) => `₹${fmtINR(v)}`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="prev" fill="#71717a" name={data?.prev_month || 'Prev'} radius={[4, 4, 0, 0]} />
              <Bar dataKey="this" fill="#f59e0b" name={data?.this_month || 'This'} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <TabBtn active={tab === 'overheads'} onClick={() => setTab('overheads')} testid="cv-tab-overheads">
          <Zap size={13} /> Overheads ({(data?.categories || []).length})
        </TabBtn>
        <TabBtn active={tab === 'raw'} onClick={() => setTab('raw')} testid="cv-tab-raw">
          <Layers size={13} /> Raw Materials ({(data?.raw_materials || []).length})
        </TabBtn>
        <TabBtn active={tab === 'fg'} onClick={() => setTab('fg')} testid="cv-tab-fg">
          <Package size={13} /> Finished Goods ({(data?.finished_goods || []).length})
        </TabBtn>
      </div>

      {loading ? (
        <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm animate-pulse">
          Crunching variance data…
        </div>
      ) : (
        <>
          {tab === 'overheads' && (
            <OverheadTable
              rows={data?.categories || []}
              onDrill={(cat) => setDrillCategory(cat)}
            />
          )}
          {tab === 'raw' && <RawMaterialTable rows={data?.raw_materials || []} />}
          {tab === 'fg' && <FinishedGoodTable rows={data?.finished_goods || []} />}
        </>
      )}

      {drillCategory && (
        <CategoryDrilldownModal
          category={drillCategory}
          bills={(data?.overhead_details || {})[drillCategory] || []}
          thisMonth={data?.this_month}
          onClose={() => setDrillCategory(null)}
        />
      )}
    </div>
  );
}

const KPI = ({ label, value, testid, color }) => (
  <div data-testid={testid} className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
    <div className="text-xs text-zinc-400">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color || 'text-zinc-100'}`}>{value}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${
      active ? 'bg-amber-600 border-amber-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
    }`}
  >{children}</button>
);

// ─── Overhead table ─────────────────────────────────────────────────────
function OverheadTable({ rows, onDrill }) {
  if (!rows || rows.length === 0) {
    return <EmptyState text="No overhead bills recorded in this window." />;
  }
  return (
    <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-zinc-300">
          <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Flag</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-right">Prev</th>
              <th className="px-3 py-2 text-right">This</th>
              <th className="px-3 py-2 text-right">Δ</th>
              <th className="px-3 py-2 text-right">Δ%</th>
              <th className="px-3 py-2 text-right">Bills</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = r.pct_change;
              const deltaClass = pct > 0 ? 'text-red-400' : pct < 0 ? 'text-green-400' : 'text-zinc-500';
              const Icon = CATEGORY_ICON[r.category] || Receipt;
              return (
                <tr
                  key={r.category}
                  data-testid={`cv-row-oh-${i}`}
                  className={`border-t border-zinc-800 hover:bg-zinc-800/40 ${r.flagged ? 'bg-amber-900/10' : ''}`}
                >
                  <td className="px-3 py-2">
                    {r.flagged ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border bg-amber-900/40 text-amber-300 border-amber-700">
                        <AlertTriangle size={10} /> FLAG
                      </span>
                    ) : <span className="text-zinc-600 text-[10px]">—</span>}
                  </td>
                  <td className="px-3 py-2 font-medium text-zinc-100 flex items-center gap-2">
                    <Icon size={12} className="text-zinc-500" /> {r.category}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400">₹{fmtINR(r.prev_month)}</td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-semibold">₹{fmtINR(r.this_month)}</td>
                  <td className={`px-3 py-2 text-right ${deltaClass}`}>₹{fmtINR(r.delta)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${deltaClass}`}>
                    {pct !== null && pct !== undefined ? `${pct > 0 ? '+' : ''}${pct}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400">{r.bill_count}</td>
                  <td className="px-3 py-2 text-right">
                    {r.bill_count > 0 && (
                      <button
                        data-testid={`cv-drill-${r.category}`}
                        onClick={() => onDrill(r.category)}
                        className="px-2 py-0.5 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-700/40 text-[10px] font-semibold flex items-center gap-1"
                      >
                        Bills <ChevronRight size={10} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Raw material table ─────────────────────────────────────────────────
function RawMaterialTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <EmptyState text="No raw-material data with rates in this window." />;
  }
  return (
    <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-zinc-300">
          <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Flag</th>
              <th className="px-3 py-2 text-left">Raw Material</th>
              <th className="px-3 py-2 text-left">Unit</th>
              <th className="px-3 py-2 text-right">Prev Rate</th>
              <th className="px-3 py-2 text-right">This Rate</th>
              <th className="px-3 py-2 text-right">Δ Rate%</th>
              <th className="px-3 py-2 text-right">Prev Qty</th>
              <th className="px-3 py-2 text-right">This Qty</th>
              <th className="px-3 py-2 text-right">Prev Spend</th>
              <th className="px-3 py-2 text-right">This Spend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = r.pct_change;
              const deltaClass = pct > 0 ? 'text-red-400' : pct < 0 ? 'text-green-400' : 'text-zinc-500';
              return (
                <tr
                  key={r.id || i}
                  data-testid={`cv-row-rm-${i}`}
                  className={`border-t border-zinc-800 hover:bg-zinc-800/40 ${r.flagged ? 'bg-amber-900/10' : ''}`}
                >
                  <td className="px-3 py-2">
                    {r.flagged ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border bg-amber-900/40 text-amber-300 border-amber-700">
                        <AlertTriangle size={10} /> FLAG
                      </span>
                    ) : <span className="text-zinc-600 text-[10px]">—</span>}
                  </td>
                  <td className="px-3 py-2 font-medium text-zinc-100">{r.name}</td>
                  <td className="px-3 py-2 text-zinc-500">{r.unit}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">{r.prev_rate !== null ? `₹${fmtINR(r.prev_rate)}` : '—'}</td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-semibold">{r.this_rate !== null ? `₹${fmtINR(r.this_rate)}` : '—'}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${deltaClass}`}>
                    {pct !== null && pct !== undefined ? `${pct > 0 ? '+' : ''}${pct}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-500">{fmt(r.prev_qty)}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">{fmt(r.this_qty)}</td>
                  <td className="px-3 py-2 text-right text-zinc-500">₹{fmtINR(r.prev_total)}</td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-semibold">₹{fmtINR(r.this_total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── FG table (unchanged) ────────────────────────────────────────────────
function FinishedGoodTable({ rows }) {
  if (!rows || rows.length === 0) return <EmptyState text="No finished-good price changes in this window." />;
  return (
    <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-zinc-300">
          <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Flag</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-right">Prev Price</th>
              <th className="px-3 py-2 text-right">This Price</th>
              <th className="px-3 py-2 text-right">Δ%</th>
              <th className="px-3 py-2 text-left">Changed On</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = r.pct_change;
              const cls = pct > 0 ? 'text-red-400' : pct < 0 ? 'text-green-400' : 'text-zinc-500';
              return (
                <tr key={r.id || i} data-testid={`cv-row-fg-${i}`} className={`border-t border-zinc-800 ${r.flagged ? 'bg-amber-900/10' : ''}`}>
                  <td className="px-3 py-2">{r.flagged ? <AlertTriangle size={12} className="text-amber-400" /> : <span className="text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 font-medium text-zinc-100">{r.name}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">₹{fmtINR(r.prev_price)}</td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-semibold">₹{fmtINR(r.this_price)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${cls}`}>{pct != null ? `${pct > 0 ? '+' : ''}${pct}%` : '—'}</td>
                  <td className="px-3 py-2 text-zinc-500 text-[11px]">{(r.changed_on || '').slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm">{text}</div>;
}

// ─── Cost per kg panel ──────────────────────────────────────────────────
function CostPerKgPanel({ cpk, prevMonth, thisMonth }) {
  const delta = cpk.total_cost_per_kg_prev && cpk.total_cost_per_kg_this
    ? (((cpk.total_cost_per_kg_this - cpk.total_cost_per_kg_prev) / cpk.total_cost_per_kg_prev) * 100).toFixed(2)
    : null;
  const deltaClass = delta > 0 ? 'text-red-400' : delta < 0 ? 'text-green-400' : 'text-zinc-300';

  return (
    <div data-testid="cv-cpk-panel" className="bg-gradient-to-br from-violet-900/20 to-indigo-900/10 border border-violet-700/40 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-zinc-100 text-base flex items-center gap-2">
            <TrendingUp size={16} className="text-violet-300" /> Cost per kg Produced
          </h3>
          <p className="text-[11px] text-zinc-400 mt-0.5">
            {prevMonth}: <span className="font-semibold text-zinc-300">{fmtINR(cpk.prev_kg)} kg</span>
            <span className="mx-2 text-zinc-600">→</span>
            {thisMonth}: <span className="font-semibold text-zinc-300">{fmtINR(cpk.this_kg)} kg</span>
            {cpk.kg_variance_pct !== null && cpk.kg_variance_pct !== undefined && (
              <span className={`ml-2 ${cpk.kg_variance_pct > 0 ? 'text-green-400' : 'text-amber-400'}`}>
                ({cpk.kg_variance_pct > 0 ? '+' : ''}{cpk.kg_variance_pct}% output)
              </span>
            )}
          </p>
        </div>
        {delta !== null && (
          <div className="text-right">
            <div className="text-[10px] text-zinc-500 uppercase">Total cost/kg</div>
            <div className={`text-xl font-bold ${deltaClass}`}>
              {delta > 0 ? '+' : ''}{delta}%
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <CPKTile label="Raw Material ₹/kg" prev={cpk.rm_cost_per_kg_prev} cur={cpk.rm_cost_per_kg_this} testid="cpk-rm" />
        <CPKTile label="Overheads ₹/kg" prev={cpk.overhead_cost_per_kg_prev} cur={cpk.overhead_cost_per_kg_this} testid="cpk-oh" />
        <CPKTile label="Total Cost ₹/kg" prev={cpk.total_cost_per_kg_prev} cur={cpk.total_cost_per_kg_this} testid="cpk-total" highlight />
      </div>

      {(cpk.per_category || []).length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase text-zinc-500 border-b border-zinc-800">Per overhead category — cost per kg</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-800/40 text-zinc-500 text-[10px] uppercase">
                <tr>
                  <th className="px-3 py-1.5 text-left">Category</th>
                  <th className="px-3 py-1.5 text-right">Prev ₹/kg</th>
                  <th className="px-3 py-1.5 text-right">This ₹/kg</th>
                  <th className="px-3 py-1.5 text-right">Δ%</th>
                  <th className="px-3 py-1.5 text-right">This Spend</th>
                </tr>
              </thead>
              <tbody>
                {cpk.per_category.map((r, i) => {
                  const cls = r.pct_change > 0 ? 'text-red-400' : r.pct_change < 0 ? 'text-green-400' : 'text-zinc-500';
                  return (
                    <tr key={i} data-testid={`cpk-row-${i}`} className={`border-t border-zinc-800 ${r.flagged ? 'bg-amber-900/10' : ''}`}>
                      <td className="px-3 py-1.5 text-zinc-200 font-medium">{r.category}</td>
                      <td className="px-3 py-1.5 text-right text-zinc-400">{r.prev_per_kg !== null ? `₹${fmt(r.prev_per_kg)}` : '—'}</td>
                      <td className="px-3 py-1.5 text-right text-zinc-100 font-semibold">{r.this_per_kg !== null ? `₹${fmt(r.this_per_kg)}` : '—'}</td>
                      <td className={`px-3 py-1.5 text-right font-semibold ${cls}`}>{r.pct_change !== null && r.pct_change !== undefined ? `${r.pct_change > 0 ? '+' : ''}${r.pct_change}%` : '—'}</td>
                      <td className="px-3 py-1.5 text-right text-zinc-500">₹{fmtINR(r.this_spend)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CPKTile({ label, prev, cur, testid, highlight }) {
  const delta = (prev && cur) ? (((cur - prev) / prev) * 100).toFixed(2) : null;
  const cls = delta > 0 ? 'text-red-400' : delta < 0 ? 'text-green-400' : 'text-zinc-300';
  return (
    <div data-testid={testid} className={`rounded-lg p-3 ${highlight ? 'bg-violet-950/40 border border-violet-700/50' : 'bg-zinc-900/60 border border-zinc-800'}`}>
      <div className="text-[10px] uppercase text-zinc-400">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className={`text-xl font-bold ${highlight ? 'text-violet-200' : 'text-zinc-100'}`}>{cur !== null ? `₹${fmt(cur)}` : '—'}</div>
        <div className="text-[10px] text-zinc-500">was ₹{prev !== null ? fmt(prev) : '—'}</div>
      </div>
      {delta !== null && (
        <div className={`text-[11px] font-semibold ${cls} mt-0.5`}>{delta > 0 ? '+' : ''}{delta}%</div>
      )}
    </div>
  );
}

// ─── Drill-down modal ───────────────────────────────────────────────────
function CategoryDrilldownModal({ category, bills, thisMonth, onClose }) {
  const total = bills.reduce((s, b) => s + (b.total_amount || 0), 0);
  return (
    <div
      data-testid="cv-drilldown-modal"
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <h3 className="font-bold text-zinc-100">{category} — {thisMonth}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">{bills.length} bills · Total ₹{fmtINR(total)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400"><CloseIcon size={16} /></button>
        </div>
        <div className="overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Invoice#</th>
                <th className="px-3 py-2 text-left">Vendor</th>
                <th className="px-3 py-2 text-left">Product / Item</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Rate</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {bills.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No bills in this category this month.</td></tr>
              ) : bills.map((b, i) => (
                <tr key={b.id || i} data-testid={`cv-bill-row-${i}`} className="border-t border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-3 py-2 text-zinc-400">{b.invoice_date || '—'}</td>
                  <td className="px-3 py-2 text-zinc-300 font-mono">{b.invoice_number || '—'}</td>
                  <td className="px-3 py-2 text-zinc-100">{b.vendor_name || '—'}</td>
                  <td className="px-3 py-2 text-zinc-300">{b.product_name || <span className="italic text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 text-right text-zinc-500">{b.quantity ? fmt(b.quantity) : '—'}</td>
                  <td className="px-3 py-2 text-right text-zinc-500">{b.unit_price ? `₹${fmtINR(b.unit_price)}` : '—'}</td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-semibold">₹{fmtINR(b.total_amount)}</td>
                  <td className="px-3 py-2 text-zinc-500 text-[11px]">{b.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
            {bills.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-zinc-700 bg-zinc-800/40">
                  <td colSpan={6} className="px-3 py-2 text-right font-semibold text-zinc-300">TOTAL</td>
                  <td className="px-3 py-2 text-right font-bold text-amber-300">₹{fmtINR(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
